'use client'

import type { Toggle as TogglePrimitive } from '@base-ui/react/toggle'
import { ToggleGroup as ToggleGroupPrimitive } from '@base-ui/react/toggle-group'
import type { VariantProps } from 'class-variance-authority'
import * as React from 'react'
import { cn } from '../lib/utils'
import { Separator } from './separator'
import { Toggle as ToggleComponent, type toggleVariants } from './toggle'

const ToggleGroupContext = React.createContext<
  VariantProps<typeof toggleVariants>
>({
  size: 'default',
  variant: 'default',
})

function ToggleGroup({
  className,
  variant = 'default',
  size = 'default',
  orientation = 'horizontal',
  children,
  ...props
}: ToggleGroupPrimitive.Props & VariantProps<typeof toggleVariants>) {
  const toggleGroupCtxValue = React.useMemo(
    () => ({ size, variant }),
    [size, variant],
  )
  return (
    <ToggleGroupPrimitive
      className={cn(
        'flex w-fit *:focus-visible:z-10',
        orientation === 'horizontal'
          ? '*:pointer-coarse:after:min-w-auto'
          : 'flex-col *:pointer-coarse:after:min-h-auto',
        variant === 'default'
          ? 'gap-0.5'
          : orientation === 'horizontal'
            ? '*:not-first:rounded-s-none *:not-first:border-s-0 *:not-last:rounded-e-none *:not-last:border-e-0'
            : '*:not-first:rounded-t-none *:not-first:border-t-0 *:not-last:rounded-b-none *:not-last:border-b-0',
        className,
      )}
      data-size={size}
      data-slot="toggle-group"
      data-variant={variant}
      orientation={orientation}
      {...props}
    >
      <ToggleGroupContext.Provider value={toggleGroupCtxValue}>
        {children}
      </ToggleGroupContext.Provider>
    </ToggleGroupPrimitive>
  )
}

function Toggle({
  className,
  children,
  variant,
  size,
  ...props
}: TogglePrimitive.Props & VariantProps<typeof toggleVariants>) {
  const ctx = React.useContext(ToggleGroupContext)

  // oxlint-disable-next-line typescript/prefer-nullish-coalescing -- intentional: empty string/undefined falls through to prop value
  const resolvedVariant = ctx.variant || variant
  // oxlint-disable-next-line typescript/prefer-nullish-coalescing, unicorn/explicit-length-check -- intentional: empty string/undefined falls through; .size is a variant string enum, not a collection
  const resolvedSize = ctx.size || size

  return (
    <ToggleComponent
      className={className}
      data-size={resolvedSize}
      data-variant={resolvedVariant}
      size={resolvedSize}
      variant={resolvedVariant}
      {...props}
    >
      {children}
    </ToggleComponent>
  )
}

function ToggleGroupSeparator({
  className,
  orientation = 'vertical',
  ...props
}: {
  className?: string
} & React.ComponentProps<typeof Separator>) {
  return (
    <Separator
      className={cn('pointer-events-none relative', className)}
      orientation={orientation}
      {...props}
    />
  )
}

export { ToggleGroup, Toggle, Toggle as ToggleGroupItem, ToggleGroupSeparator }
